import { ReactElement, useMemo, useState } from 'react';
import {
  Error,
  Prompt,
  Success,
  Tooltip,
  Typography,
  SnackbarStatus,
  useSnackbar,
  CardGrid,
  EllipsisMenu,
  ArrowDownIcon,
  Bin,
  ArrowUpIcon,
  EditIcon,
  AddPriceBandIcon,
  PriceBandIcon,
  Breadcrumbs,
} from '@weareopenr/component-library';
import { useQueryClient } from '@tanstack/react-query';
import styles from './MenuSection.module.scss';
import Divider from '../Divider';
import NewItemCard from 'src/components/NewItemCard';
import { moveSection } from 'src/api/moveSection/moveSection';
import { ISection } from 'src/types/menu.types';
import { deleteSection } from 'src/api/deleteSection/deleteSection';
import { getHierarchyNames } from 'src/helpers';
import { getPromptText } from './utilities/getPromptText';
import SectionModal from '../SectionModal';
import MenuItemCard from '../MenuItemCard';
import { ICategoryWithChildren } from 'src/components/MenuSectionItems/utilities';
import { QueryKeys } from 'src/hooks/QueryKeys';

export interface IMenuSectionProps {
  menuId: string;
  section: ICategoryWithChildren;
  handleSectionModal: (value?: string | null) => void;
  onClickNewItem: (sectionId?: string) => void;
  upDisplayOrder?: number;
  downDisplayOrder?: number;
  isAssignedToMealtime: boolean | undefined;
  tooltipPosition: 'top' | 'bottom';
  sections: ISection[];
}

const MenuSection = ({
  menuId,
  handleSectionModal,
  onClickNewItem,
  section,
  upDisplayOrder,
  downDisplayOrder,
  isAssignedToMealtime,
  tooltipPosition,
  sections,
}: IMenuSectionProps): ReactElement => {
  const { sectionId, name, items, parentSectionId, children } = section;
  const queryClient = useQueryClient();
  const { showSnackbar, hideSnackbar } = useSnackbar();
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isUpdateCategoryModalOpen, setIsUpdateCategoryModalOpen] = useState<boolean>(false);
  const [isRemovePromptVisible, setIsRemovePromptVisible] = useState<boolean>(false);
  const removePromptTitle = `Are you sure you want to delete "${name}" ${
    parentSectionId ? 'subcategory' : 'category'
  }?`;
  const removeSectionPromptSubTitle = getPromptText(name, items, children);
  const categoryBreadcrumbs = useMemo(
    () =>
      getHierarchyNames(section, sections, { id: 'sectionId', parent_id: 'parentSectionId' }).map((name) => ({
        label: name,
      })),
    [section, sections],
  );

  const handleDelete = async () => {
    setIsDeleteLoading(true);
    try {
      await deleteSection(menuId, sectionId);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
    } catch (e) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'We were unable to delete, please try again!',
      });
      setIsDeleteLoading(false);
    }
  };

  const reorderSection = async (newDisplayOrder: number) => {
    try {
      showSnackbar({
        status: SnackbarStatus.PROGRESS,
        message: 'Moving menu section',
      });
      await moveSection({ menuId, sectionId, newDisplayOrder });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
      hideSnackbar();
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'We have been unable to complete your request',
      });
    }
  };

  const itemCards = [
    ...items.map((item, index) => (
      <MenuItemCard
        {...item}
        menuId={menuId}
        itemId={item.itemId}
        parentItemId={item.parentItemId}
        sectionId={sectionId}
        displayOrderLeft={items[index - 1]?.displayOrder}
        displayOrderRight={items[index + 1]?.displayOrder}
        key={item.itemId}
      />
    )),
    <NewItemCard text="Add products" onClick={() => onClickNewItem(sectionId)} key="NewItemCard" />,
  ];

  const ellipsisMenu = (
    <EllipsisMenu
      options={[
        {
          id: 0,
          name: 'Edit',
          icon: EditIcon,
          onOptionClick: () => setIsUpdateCategoryModalOpen(true),
          dataTestId: `rename-section-${name}`,
        },
        {
          id: 1,
          name: 'Move up',
          icon: ArrowUpIcon,
          onOptionClick: () => reorderSection(upDisplayOrder),
          isDisabled: upDisplayOrder === undefined,
          dataTestId: `move-up-section-${name}`,
        },
        {
          id: 2,
          name: 'Move down',
          icon: ArrowDownIcon,
          onOptionClick: () => reorderSection(downDisplayOrder),
          isDisabled: downDisplayOrder === undefined,
          dataTestId: `move-down-section-${name}`,
        },
        {
          id: 'd-1',
          isDivider: true,
        },
        {
          id: 'add-category',
          name: 'Add category',
          icon: AddPriceBandIcon,
          onOptionClick: () => handleSectionModal(null),
        },
        {
          id: 'add-subcategory',
          name: 'Add subcategory',
          icon: PriceBandIcon,
          onOptionClick: () => handleSectionModal(sectionId),
        },
        {
          id: 'd-2',
          isDivider: true,
        },
        {
          id: 3,
          name: 'Delete',
          icon: Bin,
          onOptionClick: () => setIsRemovePromptVisible(true),
          hoverColour: 'tomato',
          dataTestId: `delete-section-${name}`,
        },
      ]}
      variant="ghost"
    />
  );

  return (
    <>
      <Prompt
        promptHeading={removePromptTitle}
        closePrompt={() => setIsRemovePromptVisible(false)}
        open={isRemovePromptVisible}
        size="small"
        backButton={true}
        backButtonText="Back"
        buttonText="Delete"
        buttonColour="tomato"
        buttonType="button"
        buttonHandler={handleDelete}
        isSubmitting={isDeleteLoading}
      >
        <Typography>{removeSectionPromptSubTitle}</Typography>
      </Prompt>
      <SectionModal
        section={section}
        open={isUpdateCategoryModalOpen}
        closeModal={() => setIsUpdateCategoryModalOpen(false)}
        menuId={menuId}
        parentSectionId={section.parentSectionId}
      />
      <Divider title={name} action={ellipsisMenu} colour={parentSectionId ? 'caviar' : 'liquorice'}>
        <div className={styles['section-title']}>
          <Breadcrumbs breadcrumbs={categoryBreadcrumbs} maxLevel={2} size="medium" />
          {isAssignedToMealtime !== undefined && !parentSectionId && (
            <Tooltip
              text={
                isAssignedToMealtime
                  ? 'This menu category has been assigned to a mealtime and is ready to be published.'
                  : 'This menu category is not assigned to a mealtime, and will not be published when using mealtimes.'
              }
              position={tooltipPosition}
            >
              {isAssignedToMealtime ? <Success className={styles['kale']} /> : <Error className={styles['tomato']} />}
            </Tooltip>
          )}
        </div>
      </Divider>

      <CardGrid data-cy="card-container">{itemCards}</CardGrid>
    </>
  );
};

export default MenuSection;
